export const RenderingStates = {
  INITIAL: 'initial',
  RUNNING: 'running',
  PAUSED: 'paused',
  FINISHED: 'finished',
};

export class RenderQueue {
  constructor() {
    this.pdfViewer = null;
    this.onIdle = null;
    this.highestPriorityPage = null;
    this.idleTimeout = null;
  }

  /**
   * @param {PDFViewer} pdfViewer
   * @returns {void} just side-effect
   */
  setViewer(pdfViewer) {
    this.pdfViewer = pdfViewer;
  }

  /**
   * @param {Object} view
   * @returns {boolean}
   */
  isHighestPriority(view) {
    return this.highestPriorityPage === view.renderingId;
  }

  renderHighestPriority(currentlyVisiblePages) {
    if (this.idleTimeout) {
      clearTimeout(this.idleTimeout);
      this.idleTimeout = null;
    }

    if (this.pdfViewer.forceRendering(currentlyVisiblePages)) {
      return;
    }

    if (this.onIdle) {
      this.idleTimeout = setTimeout(this.onIdle.bind(this), 30000);
    }
  }

  static getHighestPriority(visible, views, scrolledDown) {
    // The state has changed figure out which page has the highest priority to
    // render next (if any).
    // Priority:
    // 1 visible pages
    // 2 if last scrolled down page after the visible pages
    // 2 if last scrolled up page before the visible pages
    const visibleViews = visible.views;
    const numVisible = visibleViews.length;

    if (numVisible === 0) {
      return false;
    }

    for (let i = 0; i < numVisible; ++i) {
      const {view} = visibleViews[i];
      if (!RenderQueue.isViewFinished(view)) {
        return view;
      }
    }

    // All the visible views have rendered, try to render next/previous pages.
    if (scrolledDown) {
      const nextPageIndex = visible.last.id;

      // ID's start at 1 so no need to add 1.
      if (
        views[nextPageIndex] &&
        !RenderQueue.isViewFinished(views[nextPageIndex])
      ) {
        return views[nextPageIndex];
      }
    } else {
      const previousPageIndex = visible.first.id - 2;
      if (
        views[previousPageIndex] &&
        !RenderQueue.isViewFinished(views[previousPageIndex])
      ) {
        return views[previousPageIndex];
      }
    }

    return null;
  }

  /**
   * @param {Object} view
   * @returns {boolean}
   */
  static isViewFinished(view) {
    return view.renderingState === RenderingStates.FINISHED;
  }

  /**
   * Render a page view. This calls the appropriate function
   * based on the views state. If the view is already rendered it will return
   * false.
   * @param {Object} view
   */
  renderView(view) {
    let continueRendering;
    const state = view.renderingState;

    switch (state) {
      case RenderingStates.FINISHED:
        return false;
      case RenderingStates.PAUSED:
        this.highestPriorityPage = view.renderingId;
        view.resume();
        break;
      case RenderingStates.RUNNING:
        this.highestPriorityPage = view.renderingId;
        break;
      case RenderingStates.INITIAL:
        this.highestPriorityPage = view.renderingId;
        continueRendering = () => {
          this.renderHighestPriority();
        };
        view.draw().then(continueRendering, continueRendering);
        break;
      default:
        throw new Error('Unknown state');
    }
    return true;
  }
}

export default {RenderingStates, RenderQueue};
